import type { Reading } from '@casa/driver-sdk';
import type { Tile } from '@casa/model';
import { useTileEnvironment } from './tile_environment.ts';
import { useTileHighlight } from './tile_highlight.ts';

// A tap that sends a command, as the legacy `light`, `switch` and `action` tiles do: the tile's
// background is green-yellow until the driver answers, for at most 500 ms (the owner keeps this
// until user testing, 2026-10-15).

/** `GreenYellow`, as the legacy views had it. */
export const TAP_FEEDBACK_COLOR = '#adff2f';
/** The longest the tap feedback shows (legacy `Tap` commands). */
export const TAP_FEEDBACK_MS = 500;

const wait = (ms: number) : Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Sends a command to the driver of the tile's source, with the tap feedback; `optimistic` is shown at once. */
export type TapCommand = (command: string, argument?: unknown, optimistic?: Reading[]) => Promise<void>;

export function useTapCommand(tile: Tile, source: string | undefined) : TapCommand {
    const send = useTileEnvironment().useCommand(tile, source);
    const highlight = useTileHighlight();

    return async (command, argument, optimistic) => {
        highlight(TAP_FEEDBACK_COLOR);
        const sending = send(command, argument, optimistic);
        await Promise.race([sending.catch(() => undefined), wait(TAP_FEEDBACK_MS)]);
        highlight(undefined);
        try {
            await sending;
        } catch (error) {
            // The tile shows the failure with its warning mark; this is for the console.
            console.warn(`${tile.label ?? tile.type}: ${command}: ${error instanceof Error ? error.message : String(error)}`);
        }
    };
}
